"use client";
import {
	Badge,
	Button,
	type ButtonProps,
	CardCarousel,
	Container,
	type DefaultImageProps,
	type DefaultLinkProps,
	Heading,
	type HeadingProps,
	RichText,
	Image,
	type ImageProps,
	Link,
	type LinkProps,
	Modal,
	Price,
	type PriceProps,
	Rating,
	type RatingProps,
	Tag,
	type TagProps
} from "@pamperedchef/design-system";
import { className as classNameHelper } from "@/app/utilities/className";
import { objectHasValues } from "@/app/utilities/objectHasValues";
import { type MotionProps, motion } from "framer-motion";

import type { AddItemsToHybrisCartRequest } from "@/infrastructure/hybris";

import { AddtoCart } from "../AddToCart";
import * as styles from "./OfferCardShowcase.module.scss";

export interface OfferCardShowcaseProps<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
> extends React.HTMLAttributes<HTMLDivElement> {
	audienceTag?: TagProps;
	cardTitle: HeadingProps;
	cardDescription: string;
	badgeText?: string;
	badgeCtaLabel?: string;
	badgeCtaModal?: {
		title: string;
		titleTag: HeadingProps["tag"];
		description: string;
	};
	ctas?:
		| [LinkProps<LinkComponentProps>]
		| [LinkProps<LinkComponentProps>, LinkProps<LinkComponentProps>];
	products?: {
		image: ImageProps<ImageComponentProps>;
		title: LinkProps<LinkComponentProps>;
		rating?: RatingProps;
		price?: PriceProps;
		info: {
			country: AddItemsToHybrisCartRequest["country"];
			purchaseType: AddItemsToHybrisCartRequest["purchaseType"];
			itemNumber: string;
		};
		cta: ButtonProps;
	}[];
	motionProps?: MotionProps;
	theme?: "grid" | "slider";
	errorMessage: string | null;
	alreadyAddedMessage: string;
}

/**
 * Featured Offert Card component
 */
export function OfferCardShowcase<
	ImageComponentProps = DefaultImageProps,
	LinkComponentProps = DefaultLinkProps
>({
	audienceTag,
	alreadyAddedMessage,
	cardTitle,
	cardDescription,
	badgeText,
	badgeCtaLabel,
	badgeCtaModal,
	ctas,
	errorMessage,
	products,
	className,
	motionProps,
	theme = "grid",
	...props
}: OfferCardShowcaseProps<
	ImageComponentProps,
	LinkComponentProps
>): JSX.Element {
	const DivComponent = motionProps ? motion.div : "div";
	const badgeKind = theme === "grid" ? "inverted" : "default";
	return (
		<DivComponent
			{...props}
			{...(motionProps as any)}
			{...classNameHelper(
				styles["offer-card-showcase"],
				styles[theme],
				className
			)}
		>
			<div className={styles["content"]}>
				<div className={styles["content-text"]}>
					{audienceTag ? (
						<Tag
							{...audienceTag}
							{...classNameHelper(
								styles["audience-tag"],
								audienceTag.className
							)}
						/>
					) : undefined}
					<Heading
						{...cardTitle}
						{...classNameHelper(
							styles["title"],
							cardTitle.className
						)}
						styledAs={cardTitle.styledAs ?? "h2"}
					/>
					<RichText
						content={cardDescription}
						className={styles["description"]}
					/>
				</div>

				{badgeText && (
					<div className={styles["badge-container"]}>
						<Badge
							kind={badgeKind}
							className={styles["badge"]}
							icon={{ name: "Info" }}
							label={badgeText}
						/>
						{badgeCtaLabel && (
							<Modal
								triggerElement={
									<Button
										kind="tertiary"
										className={
											styles["see-details-trigger"]
										}
									>
										{badgeCtaLabel}
									</Button>
								}
							>
								<Heading
									tag={badgeCtaModal?.titleTag ?? "h1"}
									styledAs="h2"
								>
									{badgeCtaModal?.title}
								</Heading>
								<RichText
									className={styles["modal-text"]}
									content={badgeCtaModal?.description ?? ""}
								/>
							</Modal>
						)}
					</div>
				)}

				{ctas?.length && theme === "grid" && (
					<div className={styles["cta-container"]}>
						{ctas.slice(0, 2).map((cta, index) => (
							<Button
								key={index}
								kind={index === 0 ? "primary" : "secondary"}
								size="lg"
								onPress={() => {
									const href = (cta as any).href;
									const target = (cta as any).target;
									if (target === "_blank") {
										window.open(href, "_blank");
									} else {
										window.location.href = href;
									}
								}}
							>
								{cta.children}
							</Button>
						))}
					</div>
				)}
			</div>
			{products && products.length > 0 && (
				<>
					{theme === "grid" ? (
						<div className={styles["grid-container"]}>
							{products?.map((product, index) => (
								<article key={index}>
									<Image
										{...product.image}
										aspectRatio="1:1"
										alt={product.image.alt || ""}
									/>
									<div className={styles["product-info"]}>
										<Link<LinkComponentProps>
											kind="tertiary"
											className={styles["product-title"]}
											{...product.title}
										/>
									</div>
								</article>
							))}
						</div>
					) : (
						<div className={styles["slider"]}>
							<CardCarousel
								allowOverflow
								cta={{
									children: ctas?.[0]?.children ?? "",
									href: ctas?.[0]?.href ?? ""
								}}
								slides={
									products?.map((product, index) => (
										<article
											key={index}
											className={styles["product"]}
										>
											<Image
												{...product.image}
												aspectRatio="1:1"
												className={
													styles["product-image"]
												}
												alt={product.image.alt || ""}
											/>
											<div
												className={
													styles["product-info"]
												}
											>
												<Link<LinkComponentProps>
													kind="tertiary"
													className={
														styles["product-title"]
													}
													{...product.title}
												/>
												{objectHasValues(
													product.rating
												) && (
													<Rating
														{...product.rating}
														size="small"
													/>
												)}
												{objectHasValues(
													product.price
												) && (
													<Price
														{...product.price}
														size="small"
													/>
												)}
											</div>
											<AddtoCart
												payload={{
													quantity: `1`,
													productCode:
														product.info.itemNumber,
													purchaseType:
														product.info
															.purchaseType
												}}
												buttonProps={{
													kind: "secondary",
													size: "sm",
													...product.cta
												}}
												alreadyAddedMessage={
													alreadyAddedMessage
												}
												errorMessage={errorMessage}
												className={
													styles["product-cta"]
												}
											>
												<Button
													kind="secondary"
													size="sm"
													{...product.cta}
												/>
											</AddtoCart>
										</article>
									)) ?? []
								}
							/>
						</div>
					)}
				</>
			)}
		</DivComponent>
	);
}
